import { useState, useCallback, useRef, useEffect } from 'react';
import type { JourneyPayload, SemanticEvent, TouchEvent } from '../../../types/analyticstypes';
import { formatPlaybackTime, formatRelativeTime } from '../../../utils/analytics/format';

const TRAIL_OPACITIES = [0.6, 0.4, 0.3, 0.2, 0.1];

export default function SessionReplay({ payload }: { payload: JourneyPayload }) {
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [flashEvent, setFlashEvent] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSemanticIndexRef = useRef(-1);

  useEffect(() => {
    setCurrentTimeMs(0);
    setPlaying(false);
    prevSemanticIndexRef.current = -1;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [payload]);

  const stopPlayback = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPlaying(false);
  }, []);

  const startPlayback = useCallback(() => {
    if (currentTimeMs >= payload.sessionDurationMs) {
      setCurrentTimeMs(0);
      prevSemanticIndexRef.current = -1;
    }
    setPlaying(true);
  }, [payload, currentTimeMs]);

  const togglePlayback = useCallback(() => {
    if (playing) stopPlayback();
    else startPlayback();
  }, [playing, stopPlayback, startPlayback]);

  const restart = useCallback(() => {
    stopPlayback();
    setCurrentTimeMs(0);
    prevSemanticIndexRef.current = -1;
  }, [stopPlayback]);

  useEffect(() => {
    if (!playing) return;
    const INTERVAL_MS = 50;
    intervalRef.current = setInterval(() => {
      setCurrentTimeMs((prev) => {
        const next = prev + INTERVAL_MS * speed;
        if (next >= payload.sessionDurationMs) {
          stopPlayback();
          return payload.sessionDurationMs;
        }
        return next;
      });
    }, INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [playing, speed, payload, stopPlayback]);

  const absoluteTime = payload.sessionStartTs + currentTimeMs;

  let currentTouch: TouchEvent | null = null;
  const trailTouches: TouchEvent[] = [];
  const touches = payload.touchEvents;
  let lastIdx = -1;
  for (let i = touches.length - 1; i >= 0; i--) {
    if (touches[i].ts <= absoluteTime) {
      lastIdx = i;
      break;
    }
  }
  if (lastIdx >= 0) {
    currentTouch = touches[lastIdx];
    for (let i = 1; i <= 5 && lastIdx - i >= 0; i++) trailTouches.push(touches[lastIdx - i]);
  }

  let currentSemanticIndex = -1;
  for (let i = payload.semanticEvents.length - 1; i >= 0; i--) {
    if (payload.semanticEvents[i].ts <= absoluteTime) {
      currentSemanticIndex = i;
      break;
    }
  }

  const currentRoute =
    currentSemanticIndex >= 0
      ? payload.semanticEvents[currentSemanticIndex].route
      : payload.semanticEvents[0]?.route ?? '';

  useEffect(() => {
    if (currentSemanticIndex > prevSemanticIndexRef.current) {
      const event = payload.semanticEvents[currentSemanticIndex];
      if (event) {
        setFlashEvent(event.type as string);
        if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = setTimeout(() => setFlashEvent(null), 1200);
      }
    }
    prevSemanticIndexRef.current = currentSemanticIndex;
  }, [currentSemanticIndex, payload]);

  useEffect(() => {
    if (timelineRef.current && currentSemanticIndex >= 0) {
      const el = timelineRef.current.children[currentSemanticIndex] as HTMLElement | undefined;
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentSemanticIndex]);

  const DISPLAY_WIDTH = 300;
  const displayHeight = DISPLAY_WIDTH * (payload.screenHeight / payload.screenWidth);
  const scaleX = DISPLAY_WIDTH / payload.screenWidth;
  const scaleY = displayHeight / payload.screenHeight;

  return (
    <div className="flex flex-col md:flex-row gap-6 items-start md:items-stretch">
      <div className="flex-shrink-0 mx-auto md:mx-0" style={{ width: DISPLAY_WIDTH }}>
        <div
          className="relative rounded-2xl overflow-hidden"
          style={{
            width: DISPLAY_WIDTH,
            height: displayHeight,
            background: 'linear-gradient(180deg, #1f2937 0%, #111827 100%)',
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-white/20 text-xl font-bold text-center px-4 select-none">
              {currentRoute}
            </span>
          </div>
          {trailTouches.map((t, i) => (
            <div
              key={`trail-${i}`}
              className="absolute w-3 h-3 rounded-full bg-blue-400 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{ left: t.x * scaleX, top: t.y * scaleY, opacity: TRAIL_OPACITIES[i] ?? 0.1 }}
            />
          ))}
          {currentTouch && (
            <div
              className="absolute w-3 h-3 rounded-full bg-blue-500 shadow-lg shadow-blue-500/50 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{ left: currentTouch.x * scaleX, top: currentTouch.y * scaleY }}
            />
          )}
          {flashEvent && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none">
              <span className="bg-blue-600/80 text-white text-xs font-medium px-3 py-1 rounded-full animate-pulse">
                {flashEvent}
              </span>
            </div>
          )}
        </div>
        <div className="mt-3 bg-white rounded-lg border border-gray-200 p-3">
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={togglePlayback}
              className="bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
              title={playing ? 'Pause' : 'Play'}
            >
              {playing ? (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <polygon points="6,4 20,12 6,20" />
                </svg>
              )}
            </button>
            <button
              onClick={restart}
              className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              title="Restart"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h5M20 20v-5h-5M4 9a9 9 0 0115.32-5.32M20 15a9 9 0 01-15.32 5.32"
                />
              </svg>
            </button>
            <div className="flex gap-1">
              {[1, 2, 5].map((s) => (
                <button
                  key={s}
                  onClick={() => setSpeed(s)}
                  className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
                    speed === s ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {s}x
                </button>
              ))}
            </div>
            <span className="ml-auto text-xs font-mono text-gray-500">
              {formatPlaybackTime(currentTimeMs)} / {formatPlaybackTime(payload.sessionDurationMs)}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={payload.sessionDurationMs}
            value={currentTimeMs}
            onChange={(e) => {
              setCurrentTimeMs(Number(e.target.value));
              prevSemanticIndexRef.current = -1;
            }}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
        </div>
      </div>

      <div className="flex-1 min-w-0 w-full">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Event Timeline</h4>
        <div
          ref={timelineRef}
          className="bg-white rounded-lg border border-gray-200 overflow-y-auto"
          style={{ maxHeight: displayHeight + 40 }}
        >
          {payload.semanticEvents.map((event: SemanticEvent, i: number) => {
            const isActive = i === currentSemanticIndex;
            return (
              <div
                key={i}
                className={`flex items-start gap-3 px-3 py-2 border-b border-gray-100 text-xs transition-colors cursor-pointer ${
                  isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => {
                  setCurrentTimeMs(Math.max(0, event.ts - payload.sessionStartTs));
                  prevSemanticIndexRef.current = i - 1;
                }}
              >
                <span className="text-gray-400 font-mono w-12 shrink-0">
                  {formatRelativeTime(event.ts, payload.sessionStartTs)}
                </span>
                <div className="min-w-0">
                  <span className={`font-medium ${isActive ? 'text-blue-700' : 'text-gray-700'}`}>
                    {event.type}
                  </span>
                  <p className="text-gray-400 truncate">{event.route}</p>
                  {event.data && (
                    <p className="text-gray-400 truncate mt-0.5">
                      {Object.entries(event.data)
                        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {payload.semanticEvents.length === 0 && (
            <div className="p-6 text-center text-gray-400 text-sm">No semantic events</div>
          )}
        </div>
      </div>
    </div>
  );
}
